import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Check } from 'lucide-react'
import { CircleCheck } from 'lucide-react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ChoiceButton, ConfirmButton, SectionLabel } from '@/components/bookings/dialogKit'
import {
  checkOutBooking,
  getCustomerProfile,
  getService,
  listBookings,
  listResourcesDetailed,
  PAGE_MAX,
  updateCustomerProfile,
  type BookingRecord,
  type CheckOutResult,
} from '@/lib/api'
import { HealthFundSelect } from '@/components/customers/HealthFundSelect'
import { readApiError } from '@/lib/apiError'
import { serviceReceiptToastLine } from '@/lib/documents'
import { defaultPractitioner, practitionerHint, staffOptions, type StaffOption } from '@/lib/practitioner'
import { localDayBoundsUtc } from '@/lib/calendar'
import { fmtTime } from '@/lib/format'
import type { CalendarEvent } from '@/lib/render'
import { cn } from '@/lib/utils'

// ─── "Check out" ───────────────────────────────────────────────────────────────
//
// The session is finished. Requested 2026-08-17: mark it completed, record the
// actual finishing time, confirm the services delivered, and — "if the client has
// several services in one visit, Check Out should complete all services by
// default, with an option to complete them individually".
//
// THE FINISHING TIME IS THE MOMENT OF PRESSING, and there is no field for it.
// That keeps this inside the calendar's containing rule: the operator confirms
// WHICH services, never types a value. A session written up hours later can be
// corrected in chat, which is where anything needing a typed value goes.
//
// ONE CALL PER BOOKING, deliberately. A visit with three treatments is three
// bookings, so this loops rather than adding a batch endpoint: every existing
// status guard then applies per booking, and one service failing (already
// completed, cancelled underneath) cannot silently take the others with it. The
// result says exactly how many went through.
//
// WHO DELIVERED IT is the one other thing asked (2026-10-17): their provider
// number goes on the client's service receipt, so it must be the person
// who gave the session — pre-selected only when the data says who that was.
// When that person holds numbers with several funds, the CLIENT'S fund is asked
// too (2026-10-18) and recorded before check-out issues the receipt.

export interface CheckOutDialogProps {
  event: CalendarEvent | null
  /** Tenant timezone, for finding the rest of the day's visit. */
  timezone: string
  onOpenChange: (open: boolean) => void
  onChanged?: () => void
}

interface VisitRow {
  id: string
  serviceName: string
  start: string
  /** Already closed off, so it is listed but cannot be selected. */
  done: boolean
  serviceId?: string
  resourceIds?: string[] | null
  deliveredBy?: string | null
}

export function CheckOutDialog({ event, timezone, onOpenChange, onChanged }: CheckOutDialogProps) {
  const open = event !== null

  const [rows, setRows] = useState<VisitRow[] | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [staff, setStaff] = useState<StaffOption[]>([])
  const [deliveredBy, setDeliveredBy] = useState<string | null>(null)
  // Once the operator picks somebody, a late-arriving default must not override them.
  const [pickedByHand, setPickedByHand] = useState(false)
  // A default the data settled is shown as one line; "Change" opens the list.
  const [showAllStaff, setShowAllStaff] = useState(false)
  // Each treatment's own pool — the people set up to deliver it.
  const [servicePools, setServicePools] = useState<Record<string, string[]>>({})
  // The client's recorded fund, and the operator's pick (undefined = untouched).
  const [recordedFund, setRecordedFund] = useState<string | null>(null)
  const [fundChoice, setFundChoice] = useState<string | null | undefined>(undefined)

  const anchorDate = event ? event.start.slice(0, 10) : null

  // The rest of this customer's visit on the same day. Fetched rather than read
  // off the calendar so a service on a machine the operator has scrolled past —
  // or filtered out — is still offered.
  useEffect(() => {
    if (!open || !event || !event.customer_id || !anchorDate) {
      // No customer id (an AI-composed calendar) means no visit to look up; the
      // one booking in hand is the whole answer.
      if (open && event) {
        setRows([{ id: event.id, serviceName: event.service_name, start: event.start, done: false }])
        setSelected(new Set([event.id]))
      }
      return
    }
    let live = true
    setRows(null)
    setLoadError(null)
    setError(null)
    setBusy(false)
    setLoading(true)

    const range = localDayBoundsUtc(anchorDate, timezone)
    listBookings({
      customer_id: event.customer_id,
      from: range.from,
      to: range.to,
      limit: PAGE_MAX,
    })
      .then((page) => {
        if (!live) return
        const visit = page.items
          // Only what actually happened: a cancelled or superseded row is not
          // part of the visit and must never be offered for completion.
          .filter((b: BookingRecord) => b.status !== 'cancelled' && b.status !== 'rescheduled')
          .map((b: BookingRecord): VisitRow => ({
            id: b.id,
            serviceName: b.services?.name ?? 'Session',
            start: b.start,
            done: b.status !== 'booked',
            serviceId: b.service_id,
            resourceIds: b.resource_ids,
            deliveredBy: b.delivered_by_resource_id ?? null,
          }))
          .sort((a, b) => a.start.localeCompare(b.start))
        const found = visit.length > 0
          ? visit
          : [{ id: event.id, serviceName: event.service_name, start: event.start, done: false }]
        setRows(found)
        // All of them by default, as asked — a client who had three treatments is
        // finished with all three, and ticking two out of three is the exception.
        setSelected(new Set(found.filter((r) => !r.done).map((r) => r.id)))
      })
      .catch((e) => { if (live) setLoadError(readApiError(e)) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [open, event?.id, event?.customer_id, anchorDate, timezone])

  // Who could have delivered it. A failed read only hides the picker — it must
  // never stand between the operator and closing the session off.
  useEffect(() => {
    if (!open) return
    let live = true
    setPickedByHand(false)
    setShowAllStaff(false)
    setDeliveredBy(null)
    try {
      listResourcesDetailed({ limit: PAGE_MAX })
        .then((page) => { if (live) setStaff(staffOptions(page.items)) })
        .catch(() => { if (live) setStaff([]) })
    } catch {
      setStaff([])
    }
    return () => { live = false }
  }, [open, event?.id])

  // A failed read only hides what is recorded — the operator can still pick a fund.
  useEffect(() => {
    if (!open || !event?.customer_id) return
    let live = true
    setRecordedFund(null)
    setFundChoice(undefined)
    try {
      getCustomerProfile(event.customer_id)
        .then((p) => { if (live) setRecordedFund(p?.health_fund ?? null) })
        .catch(() => undefined)
    } catch {
      // No profile read: the picker starts at "not recorded".
    }
    return () => { live = false }
  }, [open, event?.id, event?.customer_id])

  // The services' pools, so a treatment only one person gives needs no choosing.
  useEffect(() => {
    const ids = [...new Set((rows ?? []).map((r) => r.serviceId).filter((id): id is string => Boolean(id)))]
    if (ids.length === 0) return
    let live = true
    try {
      Promise.all(ids.map((id) => getService(id).then((svc) => [id, svc.resource_ids ?? []] as const)))
        .then((pairs) => { if (live) setServicePools(Object.fromEntries(pairs)) })
        .catch(() => undefined)
    } catch {
      // No pools: the booking's own resources and the business decide instead.
    }
    return () => { live = false }
  }, [rows])

  useEffect(() => {
    if (pickedByHand || !event) return
    const visit = (rows ?? []).filter((r) => selected.has(r.id))
    const legs = visit.length > 0 ? visit : [{ id: event.id, resourceIds: event.resource_ids ?? null } as VisitRow]
    // One person for the whole visit, or nobody: two treatments with two
    // different givers are for the operator to sort out.
    const picks = legs.map((leg) => defaultPractitioner(staff, {
      recorded: leg.deliveredBy ?? null,
      resourceIds: leg.resourceIds ?? null,
      serviceResourceIds: leg.serviceId ? servicePools[leg.serviceId] ?? null : null,
    }))
    setDeliveredBy(picks.every((id) => id && id === picks[0]) ? picks[0] : null)
  }, [staff, rows, event, pickedByHand, selected, servicePools])

  const chosen = staff.find((s) => s.id === deliveredBy) ?? null
  const settled = Boolean(chosen) && !pickedByHand && !showAllStaff
  const shownFund = fundChoice === undefined ? recordedFund : fundChoice
  const asksFund = Boolean(event?.customer_id) && Boolean(chosen && chosen.funds.length > 0)

  const selectable = useMemo(() => (rows ?? []).filter((r) => !r.done), [rows])
  const alreadyDone = useMemo(() => (rows ?? []).filter((r) => r.done), [rows])
  const count = selected.size

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function confirm() {
    if (!event || busy || count === 0) return
    setBusy(true)
    setError(null)

    // Recorded FIRST: check-out issues the receipt, which reads the client's fund.
    if (asksFund && event.customer_id && fundChoice !== undefined && fundChoice !== recordedFund) {
      try {
        await updateCustomerProfile(event.customer_id, { health_fund: fundChoice })
        setRecordedFund(fundChoice)
      } catch (e) {
        setError(`Couldn't record their health fund, so nothing was checked out: ${readApiError(e)}`)
        setBusy(false)
        return
      }
    }

    const ids = selectable.filter((r) => selected.has(r.id)).map((r) => r.id)
    const failures: string[] = []
    const receipts: string[] = []
    let done = 0
    for (const id of ids) {
      try {
        const result: CheckOutResult | undefined = deliveredBy
          ? await checkOutBooking(id, undefined, { deliveredBy })
          : await checkOutBooking(id)
        const line = serviceReceiptToastLine(result?.service_receipt)
        if (line) receipts.push(line)
        done += 1
      } catch (e) {
        const row = selectable.find((r) => r.id === id)
        failures.push(`${row?.serviceName ?? 'A session'}: ${readApiError(e)}`)
      }
    }

    onChanged?.()

    if (failures.length === 0) {
      toast.success(
        done === 1 ? `${event.customer_name ?? 'Customer'} checked out` : `${done} sessions completed`,
        { description: ['Finishing time recorded.', ...receipts].join(' ') },
      )
      onOpenChange(false)
      return
    }

    // Partial success is reported as partial. Closing the dialog on a silent
    // failure would leave the operator believing the whole visit was closed off.
    if (done > 0) {
      toast.warning(`${done} of ${ids.length} completed`, { description: failures[0] })
    }
    setError(failures.join(' · '))
    setBusy(false)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onOpenChange(false) }}>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Check out</DialogTitle>
          <DialogDescription>
            {event ? `${event.customer_name ?? 'Customer'} · finishing now` : ''}
          </DialogDescription>
        </DialogHeader>

        {event ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <SectionLabel>
                {selectable.length > 1 ? 'Services delivered' : 'Service delivered'}
              </SectionLabel>

              {loading ? (
                <div className="flex items-center gap-2 py-2 text-[12.5px] text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  Finding the rest of their visit…
                </div>
              ) : loadError ? (
                <p className="text-[12.5px] text-destructive">{loadError}</p>
              ) : (
                <>
                  <div className="flex flex-col gap-1.5">
                    {selectable.map((row) => {
                      const on = selected.has(row.id)
                      return (
                        <button
                          key={row.id}
                          type="button"
                          role="checkbox"
                          aria-checked={on}
                          onClick={() => toggle(row.id)}
                          className={cn(
                            'flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors',
                            on
                              ? 'border-primary bg-primary/5 hover:bg-primary/10'
                              : 'border-border bg-card hover:bg-muted',
                          )}
                        >
                          <span
                            className={cn(
                              'flex size-4 shrink-0 items-center justify-center rounded border',
                              on ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
                            )}
                            aria-hidden
                          >
                            {on ? <Check className="size-3" /> : null}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[13.5px] font-semibold text-foreground">
                              {row.serviceName}
                            </span>
                            <span className="mt-px block text-[11.5px] text-muted-foreground">
                              {fmtTime(row.start)}
                            </span>
                          </span>
                        </button>
                      )
                    })}
                  </div>

                  {selectable.length > 1 ? (
                    <p className="text-[11.5px] text-muted-foreground">
                      All of today's services are selected. Untick any that did not go ahead.
                    </p>
                  ) : null}

                  {/* Listed, not hidden: an operator who sees two of three
                      treatments would otherwise think one had gone missing. */}
                  {alreadyDone.length > 0 ? (
                    <div className="mt-1 flex flex-col gap-0.5">
                      {alreadyDone.map((row) => (
                        <span key={row.id} className="text-[11px] text-muted-foreground">
                          {row.serviceName} — already closed off
                        </span>
                      ))}
                    </div>
                  ) : null}

                  {selectable.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-border px-3 py-2.5 text-[12px] text-muted-foreground">
                      Everything in this visit is already closed off.
                    </p>
                  ) : null}
                </>
              )}
            </div>

            {staff.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                <SectionLabel>Delivered by</SectionLabel>
                {settled && chosen ? (
                  <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <ChoiceButton
                        title={chosen.name}
                        subtitle={chosen.hasProviderNumber ? 'Provider number on file' : undefined}
                        selected
                        onClick={() => setShowAllStaff(true)}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAllStaff(true)}
                      className="shrink-0 px-1 text-[12.5px] font-semibold text-primary hover:underline"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                <div className="flex flex-col gap-1.5" role="radiogroup" aria-label="Delivered by">
                  {staff.map((option) => (
                    <ChoiceButton
                      key={option.id}
                      title={option.name}
                      subtitle={option.hasProviderNumber ? 'Provider number on file' : undefined}
                      selected={option.id === deliveredBy}
                      onClick={() => {
                        setPickedByHand(true)
                        setDeliveredBy(option.id === deliveredBy ? null : option.id)
                      }}
                    />
                  ))}
                </div>
                )}
                <p className="text-[11.5px] text-muted-foreground">{practitionerHint(chosen)}</p>
              </div>
            ) : null}

            {asksFund && chosen ? (
              <div className="flex flex-col gap-1.5">
                <SectionLabel>Client's health fund</SectionLabel>
                <HealthFundSelect
                  funds={chosen.funds}
                  value={shownFund}
                  onChange={setFundChoice}
                  practitioner={chosen}
                  disabled={busy}
                />
              </div>
            ) : null}

            {error ? <p className="text-[12.5px] text-destructive">{error}</p> : null}

            <div className="flex flex-col gap-2 border-t border-border pt-3">
              <ConfirmButton
                label={count > 1 ? `Check out ${count} sessions` : 'Check out'}
                icon={<CircleCheck className="size-4" aria-hidden />}
                busy={busy}
                disabled={loading || count === 0}
                onClick={() => void confirm()}
              />
              <p className="text-center text-[11px] text-muted-foreground">
                Marks the session completed and records now as the finishing time.
              </p>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
